/**
 * Visual primitive for "pick an issue category" card grids above the
 * inspector exception table.
 *
 * Layout: [icon]  label                [count pill]
 *
 * Wide-ish rectangular tile so labels read at a glance. Count sits on
 * the far right as a small pill, separating it from the label so the
 * tile doesn't read as "651 Sale status issues" — `Sale status` and
 * `651` are independent visual elements.
 *
 * Active state: pale aqua fill, teal border, teal text/icon, plus a
 * small teal bottom notch hinting that this tile drives the Reason
 * chip row beneath the strip.
 */
import React from 'react'
import { Box, Paper, Typography } from '@mui/material'
import { poolwerxColors } from '../../../theme/colors'
import { TAB_ACTIVE_BG } from '../../../theme/exceptionTableStyles'

// Exported so other parts of the panel (Reason chip, active-filter chip)
// can reuse the same active tint and stay in sync with the tile.
export const ISSUE_CARD_ACTIVE_BG = TAB_ACTIVE_BG
const ISSUE_CARD_ACTIVE_TILE_BG = TAB_ACTIVE_BG
export const ISSUE_CARD_ACTIVE_BORDER = poolwerxColors.tealAccent
const INACTIVE_BG = '#FFFFFF'
const INACTIVE_BORDER = 'rgba(0, 0, 0, 0.10)'
const ACTIVE_SHADOW = '0 1px 2px rgba(0,0,0,0.04)'

interface IssueCardProps {
  label: string
  count: number | null | undefined
  active: boolean
  icon?: React.ReactNode
  onClick: () => void
}

const IssueCard: React.FC<IssueCardProps> = ({
  label,
  count,
  active,
  icon,
  onClick,
}) => {
  const isZero = count === 0
  const countText = count != null ? count.toLocaleString() : '—'
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        userSelect: 'none',
        boxSizing: 'border-box',
        minWidth: 144,
        maxWidth: 240,
        minHeight: 32,
        px: 1.25,
        py: 0.5,
        borderRadius: 1.5,
        bgcolor: active ? ISSUE_CARD_ACTIVE_TILE_BG : INACTIVE_BG,
        borderColor: active ? ISSUE_CARD_ACTIVE_BORDER : INACTIVE_BORDER,
        borderWidth: 1,
        boxShadow: active ? ACTIVE_SHADOW : 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        transition:
          'background-color 120ms ease, border-color 120ms ease, transform 120ms ease',
        '&:hover': {
          bgcolor: active ? ISSUE_CARD_ACTIVE_TILE_BG : '#F4F6F8',
          borderColor: active ? ISSUE_CARD_ACTIVE_BORDER : 'rgba(0, 0, 0, 0.2)',
        },
        '&:focus-visible': {
          outline: `2px solid ${poolwerxColors.tealAccent}`,
          outlineOffset: 2,
        },
      }}
    >
      {icon ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            color: active
              ? poolwerxColors.tealAccent
              : isZero
                ? 'text.disabled'
                : 'text.secondary',
            '& svg': { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Typography
        component="span"
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          fontSize: '0.8125rem',
          fontWeight: active ? 600 : 500,
          lineHeight: 1.25,
          color: active
            ? poolwerxColors.tealAccent
            : isZero
              ? 'text.disabled'
              : 'text.primary',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          minWidth: 24,
          height: 20,
          px: 0.75,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 10,
          fontSize: '0.75rem',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
          color: active
            ? '#FFFFFF'
            : isZero
              ? 'text.disabled'
              : 'text.primary',
          backgroundColor: active
            ? poolwerxColors.tealAccent
            : isZero
              ? 'transparent'
              : 'rgba(0, 0, 0, 0.06)',
          border: active
            ? `1px solid ${poolwerxColors.tealAccent}`
            : `1px solid transparent`,
        }}
      >
        {countText}
      </Box>
    </Paper>
  )
}

export default IssueCard
